import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import StockThumb from "@/components/StockThumb";
import NumpadDialog from "@/components/NumpadDialog";
import RequesterCombobox from "@/components/RequesterCombobox";
import ServingBanner from "@/components/ServingBanner";
import QrScanDialog from "@/components/QrScanDialog";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import Lightbox from "@/components/Lightbox";
import { packSummary, packTotals, fmtQty } from "@/lib/pack";
import DepartmentChips, { DepartmentBadges } from "@/components/DepartmentChips";
import MaterialChips from "@/components/MaterialChips";
import CategoryChips from "@/components/CategoryChips";
import SupplierChips from "@/components/SupplierChips";
import { ColourBadge } from "@/components/ColourChips";
import { useColours } from "@/contexts/ColoursContext";
import AIAssistantDialog from "@/components/AIAssistantDialog";
import MachineHoursPromptDialog from "@/components/MachineHoursPromptDialog";
import {
  Search,
  AlertTriangle,
  Sparkles,
  ScanLine,
  History as HistoryIcon,
} from "lucide-react";
import DiscontinuedBadge from "@/components/DiscontinuedBadge";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "roll", label: "Rolls" },
  { key: "sheet", label: "Sheets" },
  { key: "block", label: "Blocks" },
  { key: "box", label: "Boxes" },
];

const QUICK_QTY = [
  { val: 1, label: "Take 1", style: "nb-btn-primary" },
  { val: 5, label: "Take 5", style: "nb-btn-dark" },
];

const StockOut = () => {
  const { user } = useAuth();
  const { swatchOf, swatchImageOf } = useColours();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [dept, setDept] = useState("all");
  const [material, setMaterial] = useState("all");
  const [category, setCategory] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [takeRequester, setTakeRequester] = useState("");
  // "Serving" mode — once Reception picks a person at the front desk,
  // every quick-take (-1/-5) inherits that requester without retyping.
  // Persisted in sessionStorage so a hot-reload / page refresh keeps
  // the desk sticky; cleared automatically on logout via AuthContext.
  const [servingRequester, setServingRequester] = useState(
    () => sessionStorage.getItem("plw_serving_requester") || "",
  );
  useEffect(() => {
    if (servingRequester) {
      sessionStorage.setItem("plw_serving_requester", servingRequester);
    } else {
      sessionStorage.removeItem("plw_serving_requester");
    }
  }, [servingRequester]);
  const [busyId, setBusyId] = useState(null);
  const [recent, setRecent] = useState({}); // id -> qty just taken
  const [customFor, setCustomFor] = useState(null);
  // For pack items, the "Other" numpad can enter qty in EITHER the base unit
  // (e.g. bag) OR the pack sub-unit (e.g. pcs). This flag decides which — the
  // user can toggle it from inside the numpad footer. Fix for a Production
  // incident where 6 pcs of SumaBrite Medium Mop got recorded as 6 bags.
  const [customSubMode, setCustomSubMode] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [aiFor, setAiFor] = useState(null);
  const [lightboxFor, setLightboxFor] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [hoursPromptFor, setHoursPromptFor] = useState(null);
  const [deptPickerFor, setDeptPickerFor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/stock");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  // Staff are hard-filtered server-side to their assigned departments;
  // default chip stays on "all".

  // Handle ?focus=<stock_id> coming from the Alerts panel — scroll & highlight
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || items.length === 0) return;
    const target = items.find((x) => x.id === focusId);
    if (!target) {
      toast.error("That item is no longer in the catalog");
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
      return;
    }
    setSearch("");
    setFilter("all");
    setDept("all");
    setMaterial("all");
    setCategory("all");
    setHighlightId(focusId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="stock-item-${focusId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    setTimeout(() => setHighlightId(null), 2400);
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Handle ?scan=<sku> — this is what QR labels generated on the /labels
  // page point to. Any phone camera scanning a printed QR opens this URL
  // and lands us here; we auto-run the scan match once the item list has
  // arrived from the API. Falls through to handleScan() so raw SKU
  // matching + toast messaging stays identical to the in-app scanner.
  useEffect(() => {
    const scanSku = searchParams.get("scan");
    if (!scanSku || items.length === 0) return;
    handleScan(scanSku);
    const next = new URLSearchParams(searchParams);
    next.delete("scan");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const relevantMaterials = useMemo(() => {
    if (dept === "all") return [];
    const inScope = items.filter((it) => {
      const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
      return list.includes(dept);
    });
    return Array.from(new Set(inScope.map((it) => it.material).filter(Boolean)));
  }, [items, dept]);

  const relevantCategories = useMemo(() => {
    // Categories in scope after dept/material filters — so the row narrows
    // as staff drill down (avoids showing "PPE" when they've already picked
    // Material=Lunasoft).
    const inScope = items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      return true;
    });
    return Array.from(new Set(inScope.map((it) => it.category).filter(Boolean)));
  }, [items, dept, material]);

  // Suppliers present after dept / material / category — feeds SupplierChips
  // so the list only shows suppliers relevant to the current scope.
  const relevantSuppliers = useMemo(() => {
    const inScope = items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      return true;
    });
    return Array.from(
      new Set(inScope.map((it) => it.supplier_default).filter(Boolean)),
    );
  }, [items, dept, material, category]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (it.discontinued && Number(it.current_qty || 0) <= 0) return false;
      if (filter !== "all" && it.type !== filter) return false;
      if (dept !== "all") {
        const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      if (
        supplierFilter !== "all" &&
        (it.supplier_default || "") !== supplierFilter
      )
        return false;
      if (!q) return true;
      return (
        it.name.toLowerCase().includes(q) ||
        (it.sku || "").toLowerCase().includes(q) ||
        (it.supplier_default || "").toLowerCase().includes(q) ||
        (it.material || "").toLowerCase().includes(q) ||
        (it.colour || "").toLowerCase().includes(q)
      );
    });
  }, [items, filter, search, dept, material, category, supplierFilter]);

  const jumpTo = (id) => {
    if (!items.some((x) => x.id === id)) return toast.error("Replacement isn't visible in your departments");
    setSearch("");
    setFilter("all");
    setDept("all");
    setMaterial("all");
    setCategory("all");
    setSupplierFilter("all");
    setHighlightId(id);
    setTimeout(() => {
      document.querySelector(`[data-testid="stock-item-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    setTimeout(() => setHighlightId(null), 2400);
  };

  const take = async (item, qty, noteOverride, chosenDept, subCtx, requester) => {
    if (busyId) return;
    // Multi-dept intercept: if the item spans 2+ depts and the caller didn't
    // supply one, pop the picker and let the user tap which dept they're
    // taking for. Single-dept items go straight through (backend auto-fills).
    const itemDepts = item.departments?.length
      ? item.departments
      : item.department
        ? [item.department]
        : [];
    if (!chosenDept && itemDepts.length >= 2) {
      setDeptPickerFor({ item, qty, note: noteOverride, subCtx, requester });
      return;
    }
    setBusyId(item.id);
    try {
      const payload = { qty };
      if (noteOverride) payload.notes = noteOverride;
      if (chosenDept) payload.department = chosenDept;
      // Fall back to the sticky "Serving" name when the caller didn't
      // supply an explicit requester — this is how the quick -1/-5
      // buttons pick up the Reception banner without the caller having
      // to thread the value through every call site.
      const effectiveRequester = requester?.trim() || servingRequester?.trim();
      if (effectiveRequester) payload.requester = effectiveRequester;
      // Pack-aware: when staff take individual sub-units (e.g. 1 scrub of a
      // bag of 50), qty is the fractional base amount (0.02 bag) but we also
      // stamp sub_qty=1, sub_unit="scrub" so History + Reports can display
      // the human-friendly count instead of "0.02 bag".
      if (subCtx?.sub_qty && subCtx?.sub_unit) {
        payload.sub_qty = subCtx.sub_qty;
        payload.sub_unit = subCtx.sub_unit;
      }
      const { data } = await api.post(`/stock/${item.id}/take`, payload);
      // Detect fresh threshold cross for a "just crossed low" toast with a
      // Reorder shortcut. Only fire when we actually cross (not when already
      // low), so chronically-low items don't spam the toaster.
      const oldQty = Number(item.current_qty || 0);
      const newQty = Number(data.item.current_qty || 0);
      const thr = Number(item.low_stock_threshold || 0);
      const justCrossedLow = thr > 0 && oldQty > thr && newQty <= thr;
      setItems((arr) =>
        arr.map((x) =>
          x.id === item.id ? { ...x, current_qty: data.item.current_qty, low_since: data.item.low_since } : x
        )
      );
      const displayed =
        subCtx?.sub_qty && subCtx?.sub_unit
          ? `${subCtx.sub_qty} ${subCtx.sub_unit}`
          : noteOverride || `${qty} ${item.unit}`;
      setRecent((r) => ({ ...r, [item.id]: displayed }));
      setTimeout(
        () =>
          setRecent((r) => {
            const n = { ...r };
            delete n[item.id];
            return n;
          }),
        1500
      );
      const successLbl =
        subCtx?.sub_qty && subCtx?.sub_unit
          ? `${subCtx.sub_qty} ${subCtx.sub_unit}`
          : noteOverride || qty + " " + item.unit;
      toast.success(`Took ${successLbl} · ${item.name}`);
      // Alert bell needs to know: low-stock badge may have appeared.
      window.dispatchEvent(new CustomEvent("stock-updated"));
      // Just crossed the threshold? Ping the manager with a one-tap
      // "Reorder now" shortcut into the Orders page, focused on this item.
      if (justCrossedLow) {
        toast.warning(`${item.name} just went low`, {
          description: `Now ${fmtQty(newQty)} ${item.unit} — below threshold of ${fmtQty(thr)}.`,
          duration: 8000,
          action: {
            label: "Reorder now",
            onClick: () => navigate(`/orders?focus=${item.id}`),
          },
        });
      }
      // If this item is linked to any machine as a spare, prompt for a
      // fresh hour-meter reading (skippable). Fire-and-forget lookup so it
      // doesn't slow the primary Take action.
      try {
        const { data: linked } = await api.get(
          `/machines/linked-to-stock/${item.id}`,
        );
        if (Array.isArray(linked) && linked.length > 0) {
          setHoursPromptFor({
            item,
            takeId: data?.movement?.id || null,
            qtyTaken: qty,
            unitCost: Number(data?.movement?.unit_cost ?? item.unit_cost ?? 0),
          });
        }
      } catch (_) {
        /* non-blocking */
      }
    } catch (e) {
      toast.error("Could not record — try again");
    } finally {
      setBusyId(null);
    }
  };

  const handleScan = (code) => {
    setScanOpen(false);
    // Deep-link QR codes generated on /labels encode a URL that includes
    // ?scan=<sku>. If the scanner (or the address bar on mount) hands us a
    // URL, pull the SKU out of the query string; supplier barcode labels
    // that just contain a raw SKU still work through the fallback.
    let payload = (code || "").trim();
    try {
      if (payload.includes("://") || payload.startsWith("/")) {
        const url = new URL(
          payload.startsWith("/") ? window.location.origin + payload : payload
        );
        payload = url.searchParams.get("scan") || payload;
      }
    } catch {
      /* not a URL — treat as raw SKU */
    }
    const codeLc = payload.trim().toLowerCase();
    const match =
      items.find((x) => x.id === payload.trim()) ||
      items.find((x) => (x.sku || "").toLowerCase() === codeLc) ||
      items.find((x) => (x.sku || "").toLowerCase().includes(codeLc)) ||
      items.find((x) => x.name.toLowerCase().includes(codeLc));
    if (match) {
      toast.success(`Matched: ${match.name}`);
      setSearch(match.name);
      setFilter("all");
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="stock-item-${match.id}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    } else {
      // UUID-shaped payloads that don't match a current item are almost
      // certainly stale labels — either printed from another environment
      // (preview vs prod) or the underlying item has since been deleted.
      // Show a more helpful message than "No item matches <uuid>".
      const isUuid = /^[0-9a-f-]{20,}$/i.test(payload.trim());
      const short = payload.length > 16 ? payload.slice(0, 12) + "…" : payload;
      const msg = isUuid
        ? `Label "${short}" doesn't match any current item — it may be from another environment or the item has been deleted.`
        : `No item matches "${payload}"`;
      toast.error(msg, { duration: 6000 });
      // Only pre-fill the search box with a printable payload — a raw UUID
      // pasted into the search field is just noise, so we clear it in
      // that case.
      setSearch(isUuid ? "" : payload);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="stock-out-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Quick take · click & go</div>
          <h1 className="font-display text-5xl leading-none mt-2">Take stock</h1>
        </div>
        <button
          data-testid="scan-open"
          onClick={() => setScanOpen(true)}
          className="nb-btn nb-btn-dark px-5 py-3 flex items-center gap-2"
        >
          <ScanLine size={20} strokeWidth={2.5} /> Scan
        </button>
      </div>

      <ServingBanner
        value={servingRequester}
        onChange={setServingRequester}
      />

      <div className="flex flex-col md:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <Search
            size={20}
            strokeWidth={2.5}
            className="absolute left-3.5 top-1/2 -translate-y-1/2"
          />
          <input
            data-testid="stock-search-input"
            className="nb-input pl-12 h-14 text-lg"
            placeholder="Search material, SKU, supplier…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`nb-btn px-4 py-2.5 text-sm ${
                filter === f.key ? "nb-btn-dark" : "bg-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <DepartmentChips value={dept} onChange={setDept} testidPrefix="take-dept" />
      </div>
      <div className="mb-3">
        <MaterialChips
          value={material}
          onChange={setMaterial}
          testidPrefix="take-mat"
          relevantNames={relevantMaterials}
        />
      </div>
      <div className="mb-3">
        <CategoryChips
          value={category}
          onChange={setCategory}
          testidPrefix="take-cat"
          relevantNames={relevantCategories}
        />
      </div>
      <div className="mb-6">
        <SupplierChips
          value={supplierFilter}
          onChange={setSupplierFilter}
          testidPrefix="take-supplier"
          relevantNames={relevantSuppliers}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="nb-card p-12 text-center" data-testid="empty-state">
          <Sparkles size={32} strokeWidth={2.5} className="mx-auto mb-2 text-[#228EBF]" />
          <div className="font-display text-2xl">No items match</div>
          <div className="text-sm text-[#6B6B6B] mt-1">
            Try a different filter or ask a manager to add this material.
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4"
          data-testid="stock-grid"
        >
          {filtered.map((it) => {
            const isLow =
              it.low_stock_threshold > 0 && it.current_qty <= it.low_stock_threshold;
            // Pulse the LOW badge for items that crossed the threshold in the
            // last 24 hours so a manager glancing at the wall at 6am spots
            // fresh trouble without being desensitised by chronically-low items.
            const lowSinceMs = it.low_since ? Date.parse(it.low_since) : NaN;
            const isFreshlyLow =
              isLow &&
              !Number.isNaN(lowSinceMs) &&
              Date.now() - lowSinceMs < 24 * 60 * 60 * 1000;
            const justTook = recent[it.id];
            const photos = it.image_paths?.length
              ? it.image_paths
              : it.image_path
              ? [it.image_path]
              : [];
            const rollLen = Number(it.roll_length_m) || 0;
            const showRolls = it.type === "roll" && rollLen > 0;
            const wholeRollsAvail = showRolls ? Math.floor((it.current_qty || 0) / rollLen) : 0;
            return (
              <div
                key={it.id}
                className={`nb-card p-3 flex gap-3 relative overflow-hidden transition-all self-start min-h-[190px] ${
                  highlightId === it.id
                    ? "ring-4 ring-[#FFB300] shadow-lg"
                    : ""
                }`}
                data-testid={`stock-item-${it.id}`}
              >
                <StockThumb
                  item={it}
                  size="h-24 w-24"
                  onClick={photos.length ? () => setLightboxFor(photos) : undefined}
                />
                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex items-start gap-2">
                    <div className="font-display text-lg leading-tight flex-1 pr-24">
                      {it.name}
                    </div>
                  </div>
                  {/* Top-right utility cluster — LOW badge sits leftmost so
                      it stays visible next to the AI + History icons. The
                      transient "just took" stamp lives one row below so it
                      never covers a persistent alert. */}
                  <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
                    {isLow && (
                      <div
                        className={`label-eyebrow px-1.5 py-0.5 border-2 border-black rounded flex items-center gap-1 ${
                          isFreshlyLow ? "pulse-danger" : ""
                        }`}
                        style={{ background: "#E5263F", color: "#fff" }}
                        data-testid={`low-${it.id}`}
                        title={
                          isFreshlyLow
                            ? "Crossed low-stock threshold in the last 24 hours"
                            : undefined
                        }
                      >
                        <AlertTriangle size={11} strokeWidth={2.5} /> LOW
                      </div>
                    )}
                    <button
                      data-testid={`ai-${it.id}`}
                      onClick={() => setAiFor(it)}
                      className="nb-btn bg-white p-1.5"
                      aria-label="ask AI"
                      title="Ask AI"
                    >
                      <Sparkles
                        size={14}
                        strokeWidth={2.5}
                        className="text-[#7A4DBA]"
                      />
                    </button>
                    <button
                      data-testid={`history-${it.id}`}
                      onClick={() => setHistoryFor(it)}
                      className="nb-btn bg-white p-1.5"
                      aria-label="history"
                      title="History"
                    >
                      <HistoryIcon size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                  <div className="label-eyebrow mt-1 flex flex-wrap items-center gap-2">
                    <span>
                      {it.type} · {it.size_spec || it.sku || it.category}
                      {showRolls && (
                        <> · {rollLen}{it.unit}/roll</>
                      )}
                    </span>
                    <DepartmentBadges item={it} />
                    {it.colour && (
                      <ColourBadge
                        name={it.colour}
                        swatch={swatchOf(it.colour)}
                        swatchImage={swatchImageOf(it.colour)}
                      />
                    )}
                    {it.is_hazardous && (
                      <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-[#E5263F]/15 text-[#E5263F] border border-[#E5263F]/30">
                        ⚠ hazardous
                      </span>
                    )}
                  </div>
                  {it.discontinued && (
                    <div className="mt-1">
                      <DiscontinuedBadge item={it} onUseReplacement={jumpTo} />
                    </div>
                  )}
                  <div className="font-mono-num text-2xl font-bold mt-1 leading-none">
                    {(() => {
                      // Avoid IEEE-754 tails like 6.550000000000001 —
                      // shared helper rounds to 2 dp and strips zeros.
                      return fmtQty(it.current_qty);
                    })()}
                    <span className="text-sm text-[#6B6B6B] ml-1">{it.unit}</span>
                    {showRolls && (
                      <span className="text-sm text-[#607087] ml-2 font-normal">
                        ≈ {wholeRollsAvail} roll{wholeRollsAvail === 1 ? "" : "s"}
                      </span>
                    )}
                    {(() => {
                      const pt = packTotals(it);
                      if (!pt) return null;
                      return (
                        <span className="text-sm text-[#9A6F00] ml-2 font-medium">
                          ≈ {pt.qtyTotal.toLocaleString()} {pt.unit} total
                        </span>
                      );
                    })()}
                  </div>
                  {packSummary(it) && !showRolls && (
                    <div
                      className="text-xs text-[#9A6F00] mt-1"
                      data-testid={`pack-hint-${it.id}`}
                    >
                      1 {it.unit} = {it.pack_size} {it.pack_unit}
                    </div>
                  )}

                  {showRolls && (
                    <div
                      className="flex gap-2 pt-3 items-center"
                      data-testid={`take-rolls-row-${it.id}`}
                    >
                      {[1, 2].map((n) => {
                        const meters = n * rollLen;
                        const note = `${n} roll${n === 1 ? "" : "s"} (${meters}${it.unit})`;
                        return (
                          <button
                            key={n}
                            data-testid={`take-${it.id}-roll-${n}`}
                            disabled={busyId === it.id || wholeRollsAvail < n}
                            onClick={() => take(it, meters, note)}
                            className={`nb-btn flex-1 py-2.5 text-sm ${
                              n === 1 ? "nb-btn-primary" : "nb-btn-dark"
                            }`}
                          >
                            Take {n} roll{n === 1 ? "" : "s"}
                            <span className="block text-[10px] opacity-80 font-normal -mt-0.5">
                              −{meters}{it.unit}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {(() => {
                    const packSize = Number(it.pack_size) || 0;
                    const packUnit = it.pack_unit || "";
                    const isPack = packSize > 0 && !!packUnit && !showRolls;
                    const pluralUnit = (n, u) =>
                      n === 1 ? u : u.endsWith("s") ? u : u + "s";
                    return (
                      <div
                        className={`mt-auto flex flex-col gap-1.5 ${showRolls ? "pt-2" : "pt-2.5"}`}
                      >
                        {/* Optional whole-pack row (only for pack items) —
                            appears above the always-present take row so all
                            cards share the same base footprint. */}
                        {isPack && (
                          <div className="flex gap-1.5">
                            {[1, 2].map((n) => (
                              <button
                                key={`pack-${n}`}
                                data-testid={`take-${it.id}-pack-${n}`}
                                disabled={
                                  busyId === it.id || (it.current_qty || 0) < n
                                }
                                onClick={() =>
                                  take(
                                    it,
                                    n,
                                    `${n} ${pluralUnit(n, it.unit)} (${n * packSize} ${pluralUnit(n * packSize, packUnit)})`,
                                  )
                                }
                                className={`nb-btn flex-1 py-2 text-sm ${
                                  n === 1 ? "nb-btn-primary" : "nb-btn-dark"
                                }`}
                              >
                                Take {n} {pluralUnit(n, it.unit)}
                                <span className="block text-[10px] opacity-80 font-normal -mt-0.5">
                                  = {n * packSize} {pluralUnit(n * packSize, packUnit)}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Always-present take row.
                            - Pack items: two sub-unit take buttons + Other.
                            - Non-pack items: Take 1 / Take 5 + Other.
                            - Roll items: Take 1{unit} / Take 5{unit} + Other. */}
                        <div className="flex gap-1.5">
                          {isPack
                            ? [1, 5].map((n) => (
                                <button
                                  key={`sub-${n}`}
                                  data-testid={`take-${it.id}-sub-${n}`}
                                  disabled={
                                    busyId === it.id ||
                                    (it.current_qty || 0) < n / packSize
                                  }
                                  onClick={() =>
                                    take(
                                      it,
                                      n / packSize,
                                      `${n} ${pluralUnit(n, packUnit)} (${(n / packSize).toFixed(3)} ${it.unit})`,
                                      undefined,
                                      { sub_qty: n, sub_unit: packUnit },
                                    )
                                  }
                                  className="nb-btn flex-1 py-2 text-sm bg-white"
                                >
                                  Take {n} {pluralUnit(n, packUnit)}
                                  <span className="block text-[10px] opacity-70 font-normal -mt-0.5">
                                    = {(n / packSize).toFixed(3)} {it.unit}
                                  </span>
                                </button>
                              ))
                            : QUICK_QTY.map((q) => (
                                <button
                                  key={q.val}
                                  data-testid={`take-${it.id}-${q.val}`}
                                  disabled={busyId === it.id}
                                  onClick={() => take(it, q.val)}
                                  className={`nb-btn flex-1 py-2 text-sm ${
                                    showRolls ? "bg-white" : q.style
                                  }`}
                                >
                                  {showRolls
                                    ? `Take ${q.val} ${it.unit}`
                                    : q.label}
                                </button>
                              ))}
                          <button
                            data-testid={`take-${it.id}-custom`}
                            disabled={busyId === it.id}
                            onClick={() => {
                              // Default to sub-unit mode when the item packs
                              // multiple pieces — 99% of "Other" taps on a
                              // pack item are for individual pcs, not whole
                              // packs (whole packs already have Take 1 / 2).
                              setCustomSubMode(isPack);
                              setCustomFor(it);
                            }}
                            className="nb-btn bg-white px-3 py-2 text-sm shrink-0"
                          >
                            Other
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {justTook && (
                  <div
                    className="absolute top-11 right-2 nb-stamp font-display text-sm px-2.5 py-0.5 border-2 border-black rounded-md bg-[#228EBF] text-white"
                    data-testid={`stamp-${it.id}`}
                  >
                    −{justTook}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <NumpadDialog
        open={!!customFor}
        title={
          customFor
            ? `Take from ${customFor.name}`
            : ""
        }
        subtitle={
          customFor && customFor.pack_size && customFor.pack_unit
            ? customSubMode
              ? `Custom qty — entering ${customFor.pack_unit} (${customFor.pack_size} per ${customFor.unit})`
              : `Custom qty — entering ${customFor.unit}s (whole packs)`
            : "Custom quantity"
        }
        confirmLabel="Take now"
        confirmColor="#228EBF"
        onClose={() => {
          setCustomFor(null);
          setTakeRequester("");
          setCustomSubMode(false);
        }}
        footer={
          <div className="flex flex-col gap-2">
            {customFor?.pack_size && customFor?.pack_unit && (
              <div
                className="flex gap-1.5"
                data-testid="take-custom-unit-toggle"
              >
                <button
                  type="button"
                  data-testid="take-custom-unit-base"
                  onClick={() => setCustomSubMode(false)}
                  className={`nb-btn flex-1 py-2 text-xs ${
                    !customSubMode ? "nb-btn-dark" : "bg-white"
                  }`}
                >
                  {customFor.unit || "unit"} (whole)
                </button>
                <button
                  type="button"
                  data-testid="take-custom-unit-sub"
                  onClick={() => setCustomSubMode(true)}
                  className={`nb-btn flex-1 py-2 text-xs ${
                    customSubMode ? "nb-btn-dark" : "bg-white"
                  }`}
                >
                  {customFor.pack_unit} (1 {customFor.unit} = {customFor.pack_size})
                </button>
              </div>
            )}
            <RequesterCombobox
              value={takeRequester}
              onChange={setTakeRequester}
              testid="take-requester"
            />
          </div>
        }
        onConfirm={(qty) => {
          const it = customFor;
          const req = takeRequester;
          const sub = customSubMode;
          const packSize = Number(it?.pack_size) || 0;
          setCustomFor(null);
          setTakeRequester("");
          setCustomSubMode(false);
          if (sub && packSize > 0 && it?.pack_unit) {
            // User entered qty in pack sub-units (pcs). Convert to base-unit
            // qty and stamp sub_qty/sub_unit so History + Reports display the
            // pcs count instead of a fractional bag.
            const baseQty = qty / packSize;
            take(it, baseQty, undefined, undefined, {
              sub_qty: qty,
              sub_unit: it.pack_unit,
            }, req);
          } else {
            take(it, qty, undefined, undefined, undefined, req);
          }
        }}
      />

      <QrScanDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={handleScan}
      />

      <ItemHistoryDialog
        open={!!historyFor}
        item={historyFor}
        onClose={() => setHistoryFor(null)}
      />

      <AIAssistantDialog
        open={!!aiFor}
        item={aiFor}
        onClose={() => setAiFor(null)}
      />

      {lightboxFor && (
        <Lightbox paths={lightboxFor} onClose={() => setLightboxFor(null)} />
      )}

      <MachineHoursPromptDialog
        open={!!hoursPromptFor}
        stockItem={hoursPromptFor?.item}
        takeId={hoursPromptFor?.takeId}
        qtyTaken={hoursPromptFor?.qtyTaken}
        unitCost={hoursPromptFor?.unitCost}
        onClose={() => setHoursPromptFor(null)}
      />

      {deptPickerFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="take-dept-picker"
          onClick={() => setDeptPickerFor(null)}
        >
          <div
            className="nb-card w-[min(440px,94vw)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b-2 border-black">
              <div className="label-eyebrow">Take stock</div>
              <h3 className="font-display text-2xl leading-tight">
                For which department?
              </h3>
              <div className="text-sm text-[#607087] mt-1">
                {deptPickerFor.item.name} spans multiple depts. Pick the one this take
                is charged to.
              </div>
            </div>
            <div className="p-5 flex flex-col gap-2">
              {(deptPickerFor.item.departments || []).map((d) => (
                <button
                  key={d}
                  data-testid={`take-dept-option-${d}`}
                  onClick={() => {
                    const ctx = deptPickerFor;
                    setDeptPickerFor(null);
                    take(ctx.item, ctx.qty, ctx.note, d);
                  }}
                  className="nb-btn bg-white px-4 py-4 text-left font-display text-lg hover:bg-[#F1E7FA]"
                >
                  {d}
                </button>
              ))}
              <button
                onClick={() => setDeptPickerFor(null)}
                className="nb-btn bg-white px-4 py-2 text-sm mt-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockOut;
